/**
 * useAdminMutation — Sub-sprint B (RL-FRONTEND-SHELL-V0).
 *
 * Thin wrapper for admin POST/PATCH/DELETE calls. Handles:
 *   - loading flag,
 *   - success toast,
 *   - error toast (with detail),
 *   - 401 → redirect to /login.
 *
 * No dry-run / confirmation_token in v0 — that pattern lives in
 * Sub-sprint D (`useDryRunApply`). Confirmation modals are handled by
 * the caller (e.g. `ConfirmActionDialog`) BEFORE invoking this hook.
 */
import { useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export function useAdminMutation() {
  const [loading, setLoading] = useState(false);

  const run = useCallback(async (method, path, body) => {
    setLoading(true);
    try {
      const fn = (api[method] || api.post).bind(api);
      const res = method === "delete"
        ? await api.delete(path)
        : await fn(path, body);
      toast.success("OK");
      return res.data;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "unknown";
      if (status === 401) { window.location.assign("/login"); return null; }
      toast.error(`${method.toUpperCase()} ${path} → ${status || "?"}: ${detail}`);
      throw e;
    } finally { setLoading(false); }
  }, []);

  return { run, loading };
}

export default useAdminMutation;
